import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { AlertSingleLine, Loader, SdirButton } from '@sdir/sds';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { ApplicationType } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { ApplicationLayout } from '../../Organisms';
import { ApplicationStatus } from '../../../types';
import {
  CertificateSummaryCard,
  QualificationSummaryCard,
  ContactInfoSummaryCard,
  PaymentSummaryCard,
} from '../../Molecules';
import { routes } from '../../../constants/routes';
import { ApplicationSummaryCard } from './ApplicationSummaryCard';
import {
  useCurrentApplication,
  useCurrentPerson,
  useFeatureToggles,
} from '../../../services/hooks';
import { Card, LoadWrapper } from '../../Atoms';
import { useGetCertificateName } from '../../../services/hooks/useGetCertificateName';
import { isPaymentEnabled } from '../../../helpers/applicationHelpers';
import { ToggleRequirementList } from '../../../constants/permissions';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { getReceiptLanguage } from '../../../helpers/languageHelpers';

export const ApplicationPage = () => {
  const intl = useIntl();
  const { application, refresh: refreshApplication } = useCurrentApplication();
  const navigate = useNavigateWithLang();
  const currentUser = useCurrentUser();
  const featureToggle = useFeatureToggles();
  const {
    person: { result: currentPerson },
  } = useCurrentPerson();
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | Error>();
  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');
  const heading = useGetCertificateName(application.result?.certificate || '');
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromSuccessfulPayment = searchParams.get('from') === 'paymentstep';
  const showPaymentButton =
    !fromSuccessfulPayment && isPaymentEnabled(application?.result?.applicationStatus || '');
  const showPaymentReceipt = !!(
    application?.result?.payment?.paymentId && application?.result?.payment?.paid
  );
  const isStatusRequestMoreInfo =
    application?.result?.applicationStatus === ApplicationStatus.RequestMoreInformation;

  const showRequirementList = !isToggleOn(
    featureToggle?.result,
    ToggleRequirementList,
    currentPerson?.personalIdentificationNumber || ''
  );

  const messageItem = useMemo(() => {
    if (application.result?.messages && application.result?.messages?.length > 0) {
      return application.result?.messages?.[application.result?.messages.length - 1];
    }
    return undefined;
  }, [application.result?.messages]);

  const timeoutExpired = useMemo(() => {
    const date = application?.result?.timeoutDate;
    if (!date) return false;
    const today = moment();
    const timeout = moment(date);
    return timeout?.isBefore(today);
  }, [application?.result?.timeoutDate]);

  const goToPaymentStep = useCallback(() => {
    const route = application?.result?.payment?.chargeId
      ? routes.application.payment
      : routes.application.summary;
    navigate(generatePath(route, { applicationUid: application.result?.uid || '' }));
  }, [application.result, navigate]);

  const downloadPaymentReceipt = useCallback(() => {
    const token = currentUser?.access_token || '';
    const url = `${process.env.REACT_APP_API_URL_PAYMENT_WINDOW}payment/receiptDocument`;
    setDownloading(true);
    setError(undefined);
    axios
      .post(
        url,
        {
          id: application?.result?.payment?.paymentId,
          language: { language: getReceiptLanguage(intl?.locale || '') },
        },
        {
          responseType: 'blob',
          headers: {
            'Accept-Language': intl?.locale || 'no',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response: AxiosResponse) => {
        if (response.status >= 400 && response.status < 600) {
          const err = new Error(response.statusText);
          setError(err);
          throw err;
        }
        const bloburl = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/octet-stream' })
        );
        return bloburl;
      })
      .then((blobUrl: string) => {
        const link = document.createElement('a');
        link.href = blobUrl;
        const paymentDate = application?.result?.payment?.paidDate?.substring(0, 10);
        const filename = `Payment_Receipt_${paymentDate}.pdf`;
        link.setAttribute('download', filename);

        const clickHandler = () => {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            link.removeEventListener('click', clickHandler);
            document.body.removeChild(link);
            setDownloading(false);
          }, 150);
        };

        document.body.appendChild(link);
        link.addEventListener('click', clickHandler, false);
        link.click();
      })
      .catch((e: AxiosError) => {
        setDownloading(false);
        setError(e);
      });
  }, [application?.result, currentUser, intl?.locale]);

  const handleUploadDocumentation = useCallback(() => {
    navigate(
      generatePath(routes.application.uploadMissingDocumentation, {
        applicationUid: application.result?.uid || '',
      })
    );
  }, [application.result?.uid, navigate]);

  const onExitClick = () => {
    if (isSid) {
      navigate(routes.sidDashboard);
    } else {
      navigate(routes.certificateOverview);
    }
  };

  useEffect(() => {
    refreshApplication?.();
  }, []);

  return (
    <ApplicationLayout
      topHeading={intl.formatMessage({ id: 'select.certificate.h1' })}
      heading={heading}
      exitLabel={intl.formatMessage({ id: isSid ? 'general.form.goback' : 'general.form.back' })}
      onExitClick={onExitClick}
      mainContentMaxWidth="100%"
      hideNavigation
    >
      <LoadWrapper
        loading={application.loading}
        error={application.error}
        errorTitleId="application.error.getown"
      >
        {application.loading && <Loader />}
        {application.result && (
          <>
            {timeoutExpired && (
              <TimeoutAlert>
                <AlertSingleLine
                  type="warn"
                  title={intl.formatMessage({ id: 'application.warning.title.timeoutExpired' })}
                  text={intl.formatMessage({
                    id: 'application.warning.description.timeoutExpired',
                  })}
                  hideClose
                />
              </TimeoutAlert>
            )}
            <MainContent>
              <FirstSection>
                <ApplicationSummaryCard
                  application={application.result}
                  successfulPayment={fromSuccessfulPayment}
                />
                {showPaymentButton && (
                  <ButtonWrapper>
                    <SdirButton
                      text={intl.formatMessage({ id: 'go.to.paymentStep.button' })}
                      htmlType="button"
                      type="default"
                      onClick={goToPaymentStep}
                    />
                  </ButtonWrapper>
                )}
                {showPaymentReceipt && (
                  <ButtonWrapper>
                    <SdirButton
                      text={intl.formatMessage({ id: 'download.paymentreceipt.button' })}
                      htmlType="button"
                      type="default"
                      onClick={downloadPaymentReceipt}
                      disabled={downloading}
                    />
                  </ButtonWrapper>
                )}
                {error && <ErrorMessage error={error} />}
                {/* 
                  Hidden for now (task 20693), but should be kept here for easy reintroduction later
                  applicationStatus === SimplifiedApplicationStatus.Processing && <WhatsNextCard />
                  */}
              </FirstSection>
              <SideSection>
                {isStatusRequestMoreInfo ? (
                  <>
                    <MissingDocumentationCard>
                      <h1>{intl.formatMessage({ id: 'application.missing.documentation' })}</h1>
                      <p>{messageItem?.content || ''}</p>
                    </MissingDocumentationCard>
                    <NavButton>
                      <SdirButton
                        type="primary"
                        text={intl.formatMessage({
                          id: 'application.upload.missing.documentation',
                        })}
                        onClick={handleUploadDocumentation}
                      />
                    </NavButton>
                  </>
                ) : (
                  <>
                    {application.result.applicationType && (
                      <div className="certificate-summary">
                        <CertificateSummaryCard
                          type={application.result.applicationType as ApplicationType}
                          certificate={application.result.certificate || ''}
                        />
                      </div>
                    )}
                    {showRequirementList && (
                      <div className="summary-card">
                        <QualificationSummaryCard
                          title={intl.formatMessage({ id: 'newapp.steps.qualifications' })}
                        />
                      </div>
                    )}
                    <div className="summary-card">
                      <ContactInfoSummaryCard />
                    </div>
                    {application.result.payment && application.result.payment.paid && (
                      <div className="summary-card">
                        <PaymentSummaryCard
                          certificateCode={application.result.certificate || ''}
                        />
                      </div>
                    )}
                    {!isSid && (
                      <NavButton>
                        <SdirButton
                          onClick={() => navigate(routes.certificateOverview)}
                          type="primary"
                          text={intl.formatMessage({ id: 'certificate.overview' })}
                          htmlType="button"
                        />
                      </NavButton>
                    )}
                  </>
                )}
              </SideSection>
            </MainContent>
          </>
        )}
      </LoadWrapper>
    </ApplicationLayout>
  );
};

const MissingDocumentationCard = styled(Card)`
  margin: 0 0 0 20rem;
  h1 {
    font-size: 24px;
  }
  p {
    white-space: pre-wrap;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    margin: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1em;
`;

const TimeoutAlert = styled.div`
  margin-bottom: 20px;
`;

const MainContent = styled.div`
  display: flex;
  max-width: 100%;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    flex-direction: column;
  }
`;

const NavButton = styled.div`
  float: right;
  margin-top: 3rem;
`;
const FirstSection = styled.div`
  width: 40%;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
  }
`;

const SideSection = styled.div`
  width: 60%;
  .certificate-summary {
    margin: 0 0 0 20rem;
  }
  .summary-card {
    margin: 3rem 0 0 20rem;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;

    .certificate-summary {
      margin: 0;
    }
    .summary-card {
      margin: 3rem 0 0 0;
    }
  }
`;
